/**
 * Month arithmetic for the aircraft activity calendar (#803).
 *
 * Kept apart from the component so the paging bounds and the grid layout can be
 * exercised directly — dates are where off-by-one errors hide.
 *
 * Everything here is plain YYYY-MM / YYYY-MM-DD strings. The days are NWT local
 * dates computed by the backend, so constructing JS Dates from them locally
 * would reintroduce the timezone question the backend already settled.
 */

const pad = (n: number): string => String(n).padStart(2, '0');

const parseMonth = (month: string): { year: number; month: number } => {
  const match = /^(\d{4})-(\d{2})$/.exec(month ?? '');
  if (!match) throw new Error('month must be YYYY-MM');
  return { year: Number(match[1]), month: Number(match[2]) };
};

/** Step a YYYY-MM month by whole months, rolling the year as needed. */
export function shiftMonth(month: string, delta: number): string {
  const { year, month: m } = parseMonth(month);
  const zeroBased = (year * 12 + (m - 1)) + delta;
  return `${Math.floor(zeroBased / 12)}-${pad((zeroBased % 12) + 1)}`;
}

/**
 * May the user page forward from here? Only while behind the current month —
 * there is no activity in the future to show.
 */
export function canGoForward(month: string, currentMonth: string): boolean {
  return month < currentMonth;
}

/** Days in the month. Month is 1-based. */
const daysInMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

/**
 * The month laid out as full weeks beginning on Sunday, days as YYYY-MM-DD and
 * padding as null so every row is seven cells.
 */
export function monthGrid(month: string): Array<Array<string | null>> {
  const { year, month: m } = parseMonth(month);
  const firstWeekday = new Date(Date.UTC(year, m - 1, 1)).getUTCDay();
  const total = daysInMonth(year, m);

  const cells: Array<string | null> = [
    ...Array(firstWeekday).fill(null),
    ...Array.from({ length: total }, (_, i) => `${year}-${pad(m)}-${pad(i + 1)}`),
  ];

  while (cells.length % 7 !== 0) cells.push(null);

  const weeks: Array<Array<string | null>> = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function monthLabel(month: string): string {
  const { year, month: m } = parseMonth(month);
  return `${MONTH_NAMES[m - 1]} ${year}`;
}
